import { AsyncPipe, NgClass, NgFor, NgIf, SlicePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewContainerRef,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  GlobalMessageService,
  GlobalMessageType,
  Product,
  RoutingService,
  TranslatePipe,
  UrlPipe,
} from '@spartacus/core';
import {
  BREAKPOINT,
  BreakpointService,
  MediaComponent,
  ProductListItemContext,
  ProductListItemContextSource,
  ProductListOutlets,
} from '@spartacus/storefront';
import { escape } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { PlpPopupService } from 'src/app/service/plp-popup/plp-popup.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { PLP_DEALER_PRICE, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { RemoveImageTitleAttrDirective } from '../../../../../shared/remove-title-attr.directive';
import { ErpPromotionalTextComponent } from '../../../promotional-text/erp-promotional-text/erp-promotional-text.component';
import { PromotionalTextComponent } from '../../../promotional-text/promotional-text.component';
import { POINTER_EVENTS_NONE_CLASS, TooltipIconComponent } from '../../../tooltip-icon/tooltip-icon.component';
import { TooltipTextComponent } from '../../../tooltip-text/tooltip-text.component';

@Component({
  selector: 'app-product-list-item',
  templateUrl: './product-list-item.component.html',
  styleUrls: ['./product-list-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    ProductListItemContextSource,
    {
      provide: ProductListItemContext,
      useExisting: ProductListItemContextSource,
    },
  ],
  imports: [
    MediaComponent,
    RemoveImageTitleAttrDirective,
    TooltipTextComponent,
    NgClass,
    NgIf,
    NgFor,
    PromotionalTextComponent,
    RouterLink,
    ErpPromotionalTextComponent,
    TooltipIconComponent,
    MatIcon,
    MatTooltip,
    AsyncPipe,
    SlicePipe,
    UrlPipe,
    TranslatePipe,
  ],
})
export class ProductListItemComponent implements OnInit, OnDestroy, OnChanges {
  readonly ProductListOutlets = ProductListOutlets;
  @Input() product: any;
  @Input() dealerPrices: DealerPrice[];
  @Input() isSearchPage: boolean;
  @Input() position: number;
  @Input() hasDealerPrice: boolean;
  @Input() basepriceHidden: boolean;
  showSrp = false;
  hasDealerPriceSubs = true;
  dealerPriceCheckerSubscription$: Subscription;
  srpSubscription$: Subscription;
  productCodesList: string[] = [];
  isMobile = false;
  csTabletWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  hasColor: string[] = [];
  isPlpPopupEnabled: boolean;
  breakpointSubscription$: Subscription;
  isMobileSize: boolean;

  activeCountry$: Observable<string> = this.countryService.getActive();

  noPointerEventsOnElement: string;
  tooltipIconConfig$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) => this.tooltipIconService.getTooltipIconConfig(activeCountry, PLP_DEALER_PRICE)),
    tap((tooltipInfoConfig) => (this.noPointerEventsOnElement = tooltipInfoConfig.noPointerEventsOnElement))
  );
  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );
  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );
  businessType: string;
  protected readonly escape = escape;

  constructor(
    protected productListItemContextSource: ProductListItemContextSource,
    protected modalService: StandardModalService,
    protected el: ElementRef,
    protected vcr: ViewContainerRef,
    private cartService: ActiveCartFacade,
    private messageService: GlobalMessageService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private gtmService: GoogleTagManagerService,
    private dealerPriceChecker: DealerPriceCheckerService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private storageService: StorageService,
    private checkoutServiceExtends?: CheckoutServiceExtends,
    private currentBusinesstype?: CurrentBusinessType,
    public deviceDetectorService?: DeviceDetectorService,
    private plpPopupService?: PlpPopupService
  ) {}

  ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.product) {
      this.productListItemContextSource.product$.next(this.product);
    }
  }
  addToCart(code: string, event: { stopPropagation: () => void }) {
    this.cartService
      .getEntry(code)
      .subscribe(() => {
        this.cartService.addEntry(code, 1);
        this.messageService.add(
          {
            key: 'cart.deleteAllModal.deleteSuccess',
          },
          GlobalMessageType.MSG_TYPE_CONFIRMATION
        );
        const element: HTMLElement = document.getElementsByClassName('BottomHeaderSlot')[0] as HTMLElement;
        element.scrollIntoView();
      })
      .unsubscribe();
    event.stopPropagation();
    return false;
  }

  // tslint:disable-next-line:use-lifecycle-interface
  ngOnInit() {
    this.isPlpPopupEnabled = this.componentService.plpPopupEnabled$.getValue();
    this.isMobile = window.innerWidth < this.csTabletWidth;
    this.getInitSrpToggleValue();

    this.breakpointSubscription$ = this.breakpointService.breakpoint$.subscribe((breakpoint) => {
      this.isMobileSize = breakpoint !== BREAKPOINT.lg && breakpoint !== BREAKPOINT.xl;
    });

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPriceSubs = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    setTimeout(() => {
      this.hasDealerPriceSubs = this.hasDealerPrice;
    }, 1000);
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  plpGridModalScroll(productValue: any): void {
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.PLP_GRID_CART_MODAL, this.el, this.vcr, {
        product: productValue,
        hasShowSrp: this.showSrp,
      })
      ?.pipe(take(1))
      .subscribe(() => {});
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  dealerPrice(productCode: string): string {
    this.dealerPrices = this.componentService.plpDealerPrices$.getValue();
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  replacement(event: any, productReplacement: any) {
    event.stopPropagation();
    this.gtmProductClick(productReplacement, this.position, true);
  }

  goToPdp(product: Product, event: any) {
    if (!this.hasPointerEventsNone(event.target)) {
      this.routingService.go({
        cxRoute: 'product',
        params: product,
      });
      this.gtmProductClick(product, this.position);
    }
  }

  getImageRendition(image: any) {
    let size: string;
    if (this.isMobile) {
      size = ShimanoUtilMethod.imageRendition(98);
    } else {
      size = ShimanoUtilMethod.imageRendition(80);
    }

    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  getColorImageRendition(image: any, code: string) {
    this.hasColor.push(code);
    let size: string;
    size = ShimanoUtilMethod.imageRendition(40);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  hasProdColor(code: string) {
    if (this.hasColor.indexOf(code) >= 0) {
      return true;
    } else {
      return false;
    }
  }

  dealerPriceData(productCode: string): DealerPrice {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceData(this.dealerPrices, productCode);
    }
  }

  hasPointerEventsNone(element: Element): boolean {
    const closestElement = element.closest(this.noPointerEventsOnElement);
    return closestElement && closestElement.classList.contains(POINTER_EVENTS_NONE_CLASS);
  }

  gtmProductClick(product: any, position: number, replacement = false) {
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };
    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  get replacementHeight(): number {
    const replacementOfElement = document.getElementsByClassName(
      ['replacement-of', this.product.code].join(' ')
    )[0] as HTMLElement;
    const replacedByElement = document.getElementsByClassName(
      ['replaced-by', this.product.code].join(' ')
    )[0] as HTMLElement;
    let replacementOfElementHeight = 0;
    let replacedByElementHeight = 0;
    if (replacementOfElement) {
      replacementOfElementHeight = replacementOfElement.clientHeight;
    }
    if (replacedByElement) {
      replacedByElementHeight = replacedByElement.clientHeight;
    }
    return Math.max(replacementOfElementHeight, replacedByElementHeight);
  }

  get dealerPriceWidth(): number {
    const srpWrapperElement = document.getElementsByClassName(
      ['srp-wrapper', this.product.code].join(' ')
    )[0] as HTMLElement;
    let dealePriceElementWidth = 0;
    if (srpWrapperElement) {
      dealePriceElementWidth = srpWrapperElement.getBoundingClientRect().width;
    }
    return dealePriceElementWidth;
  }

  get detailsWrapperWidth(): number {
    const detailsWrapperElement = document.getElementsByClassName(
      ['details-wrapper', this.product.code].join(' ')
    )[0] as HTMLElement;
    let detailsWrapperWidth = 0;
    if (detailsWrapperElement) {
      detailsWrapperWidth = detailsWrapperElement.getBoundingClientRect().width;
    }
    return detailsWrapperWidth;
  }

  get replacementHeightOnTablet$(): Observable<number> {
    return this.breakpointService.breakpoint$.pipe(
      map((breakpoint) =>
        breakpoint === BREAKPOINT.md || breakpoint === BREAKPOINT.lg ? this.replacementHeight : null
      )
    );
  }

  get dealerPriceWidthOnDesktop$(): Observable<number> {
    return this.breakpointService.breakpoint$.pipe(
      map((breakpoint) => (breakpoint === BREAKPOINT.lg ? this.dealerPriceWidth : null))
    );
  }

  get detailsWrapperWidthOnTabletMobile$(): Observable<number> {
    return this.breakpointService.breakpoint$.pipe(
      map((breakpoint) =>
        breakpoint === BREAKPOINT.sm || breakpoint === BREAKPOINT.md ? this.detailsWrapperWidth : null
      )
    );
  }

  get isColorVariantMarginTopLonger$(): Observable<boolean> {
    const nameElement = document.getElementsByClassName(['prod-name', this.product.code].join(' '))[0] as HTMLElement;
    const compStylesNameElement = window.getComputedStyle(nameElement);
    return of(nameElement.clientHeight == parseFloat(compStylesNameElement.getPropertyValue('line-height')));
  }

  get isColorVariantMarginTopAuto$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(map((breakpoint) => (breakpoint === BREAKPOINT.xl ? true : null)));
  }

  isSkuTruncated(): boolean {
    const skuTruncateElement = document.getElementsByClassName(
      ['sku-truncate', this.product.code].join(' ')
    )[0] as HTMLElement;
    return skuTruncateElement.scrollWidth > skuTruncateElement.clientWidth;
  }

  isSrpWrapped(): boolean {
    const srpWrapperElement = document.getElementsByClassName('srp-wrapper')[0] as HTMLElement;
    const srpElementHeight = srpWrapperElement?.clientHeight;
    return srpElementHeight > 40;
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
    if (this.breakpointSubscription$) {
      this.breakpointSubscription$.unsubscribe();
    }
  }
}
